
import {useState, useEffect, useMemo} from 'react';
import Grid from "@mui/material/Grid";
import {
  COLOR_MILANO_RED
} from "../../constants/styles";
import { Typography } from "../../UI/Typography";
import { dayjs } from "../../services/dayjs";
import { getDefaultDate } from "../../helpers/getDefaultDate";
import { DatePicker } from "../../UI/DatePicker";
import { Network } from "../../services/NetworkApi";

const getDateValue = (date?: dayjs.Dayjs | null) => date?.toISOString() || undefined;

export interface MatchingRoundDates {
    matchingStartDate: dayjs.Dayjs | null | undefined;
    matchingEndDate: dayjs.Dayjs | null | undefined;
    activationStartDate: dayjs.Dayjs | null | undefined;
    activationEndDate: dayjs.Dayjs | null | undefined;
}

export interface IProps {
  setValue?: (value: string | boolean | null, field: string) => void;
  matchingDatesError?: boolean;
  closedArchivedError?: boolean;
  subnetwork?: Network;
  matchingRoundStatus?: string | null;
  matchingStartDate?: string;
  matchingEndDate?: string;
  activationStartDate?: string;
  activationEndDate?: string;
}


export const MatchingDatesPartial: React.FC<IProps> = (props) => {
  const { setValue, subnetwork, matchingDatesError, closedArchivedError } = props;

  const [matchingDates, setMatchingDates] = useState<MatchingRoundDates>({
    matchingStartDate: null,
    matchingEndDate: null,
    activationStartDate: null,
    activationEndDate: null,
  });

  const matchingRoundStatus = useMemo(() => (props.matchingRoundStatus || subnetwork?.matchingRoundStatus), [props.matchingRoundStatus, subnetwork]);
  const isClosedOrAchived = useMemo(() => (matchingRoundStatus === "CLOSED" || matchingRoundStatus === "ARCHIVED"), [matchingRoundStatus]);
  // const minDateForClosedAndArchived =  dayjs(new Date());


  useEffect(() => {
    if (subnetwork) {
      const { matchingStartDate, matchingEndDate, activationStartDate, activationEndDate } = subnetwork;
      setMatchingDates({
        matchingStartDate: getDefaultDate(matchingStartDate),
        matchingEndDate: getDefaultDate(matchingEndDate),
        activationStartDate: getDefaultDate(activationStartDate),
        activationEndDate: getDefaultDate(activationEndDate),
      });
    }
    const { matchingStartDate, matchingEndDate, activationStartDate, activationEndDate} = props;
    if(matchingStartDate || matchingEndDate || activationStartDate || activationEndDate) {
      setMatchingDates({
        matchingStartDate: getDefaultDate(matchingStartDate),
        matchingEndDate: getDefaultDate(matchingEndDate),
        activationStartDate: getDefaultDate(activationStartDate),
        activationEndDate: getDefaultDate(activationEndDate),
      });
    }
    return () => {};
  }, [subnetwork]);

  const handleMatchingStartDateChange = (date: string | null) => {
    const newDate = getDefaultDate(date);
    setMatchingDates({
    ...matchingDates,
      matchingStartDate: !!newDate ? newDate : null,
    });
    setValue && setValue(!!newDate ? newDate.format("YYYY-MM-DD") : null, 'matchingStartDate');
  };

  const handleMatchingEndDateChange = (date: string | null) => {
    const newDate = getDefaultDate(date);
    setMatchingDates({
    ...matchingDates,
      matchingEndDate: !!newDate ? newDate : null,
    });
    setValue && setValue(!!newDate ? newDate.format("YYYY-MM-DD") : null, 'matchingEndDate');
  };

  const handleActivationStartDateChange = (date: string | null) => {
    const newDate = getDefaultDate(date);
    setMatchingDates({
    ...matchingDates,
      activationStartDate: !!newDate? newDate : null,
    });
    setValue && setValue(!!newDate ? newDate.format("YYYY-MM-DD") : null, 'activationStartDate');
  };

  const handleActivationEndDateChange = (date: string | null) => {
    const newDate = getDefaultDate(date);
    setMatchingDates({
    ...matchingDates,
      activationEndDate: !!newDate? newDate : null,
    });
    setValue && setValue(!!newDate ? newDate.format("YYYY-MM-DD") : null, 'activationEndDate');
  };

  

  return (
    <Grid container gap={2} pt={1} pb={2} key={subnetwork?.l2Id}>
      {isClosedOrAchived && 
        <Grid container gap={3}>
          <Typography>This matching round is {matchingRoundStatus?.toLocaleLowerCase()}. You can't select past dates. </Typography>
        </Grid>
      }
      <Grid container gap={3}>
        <Grid item xs={5.5}>
          Activation Start Date:
          <DatePicker
            key={String(matchingDates.activationStartDate)}
            onChange={handleActivationStartDateChange}
            value={getDateValue(matchingDates.activationStartDate)}
            maxDate={getDateValue(matchingDates.matchingStartDate)}
            customWidth='100%'
          />
        </Grid>
        <Grid item xs={5.5}>
          Matching Start Date:
          <DatePicker
          key={String(matchingDates.matchingStartDate)}
          onChange={handleMatchingStartDateChange}
          value={getDateValue(matchingDates.matchingStartDate)}
          minDate={getDateValue(matchingDates.activationStartDate)}
          maxDate={getDateValue(matchingDates.matchingEndDate)}
          customWidth='100%'
          />
        </Grid>
      </Grid>
      <Grid container gap={3}>
        
        <Grid item xs={5.5}>
          Matching End Date:
          <DatePicker
            key={String(matchingDates.matchingEndDate)}
            onChange={handleMatchingEndDateChange}
            value={getDateValue(matchingDates.matchingEndDate)}
            minDate={getDateValue(matchingDates.matchingStartDate)}
            maxDate={getDateValue(matchingDates.activationEndDate)}
            customWidth='100%'
          />
        </Grid>
        <Grid item xs={5.5}>Activation End Date: 
          <DatePicker
            key={String(matchingDates.activationEndDate)}
            onChange={handleActivationEndDateChange}
            value={getDateValue(matchingDates.activationEndDate)}
            minDate={getDateValue(matchingDates.matchingEndDate)}
            customWidth='100%'
          />
        </Grid>
      </Grid>

      {
        matchingDatesError &&
        <Grid container gap={3}>
          <Typography color={COLOR_MILANO_RED}>Please select all dates or none of them.</Typography>
        </Grid>
      }
      {
        closedArchivedError &&
        <Grid container gap={3}>
          <Typography color={COLOR_MILANO_RED}>At least Matching End Date and Activation End Date should be set in the future</Typography>
        </Grid>
      }
    </Grid>
  );
  };